import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  CircularProgress,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissions as usePermissionsContext } from '../../contexts/PermissionsContext';

const ProjectPermissionsPanel = ({ project }) => {
  const { loading, error, getProjectAccessDetails } = usePermissionsContext();
  const permissions = usePermissions(project);
  const [accessDetails, setAccessDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const loadAccessDetails = useCallback(async () => {
    if (!project) return;
    
    setLoadingDetails(true);
    try {
      const details = await getProjectAccessDetails(project.id);
      setAccessDetails(details);
    } catch (err) {
      console.error('Error loading access details:', err);
    } finally {
      setLoadingDetails(false);
    }
  }, [project, getProjectAccessDetails]);

  useEffect(() => {
    if (project) {
      loadAccessDetails();
    }
  }, [project, loadAccessDetails]);

  if (!project) {
    return (
      <Alert severity="info">
        Select a project to view permissions
      </Alert>
    );
  }

  const getRoleColor = (role) => {
    switch (role) {
      case 'maintainer': return 'success';
      case 'contributor': return 'warning';
      case 'viewer': return 'default';
      default: return 'default';
    }
  };

  const getPermissionIcon = (hasPermission) => {
    return hasPermission ? <CheckIcon color="success" /> : <ErrorIcon color="error" />;
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" display="flex" alignItems="center" gap={1}>
            <SecurityIcon />
            Project Permissions
          </Typography>
          <Tooltip title="Refresh permissions">
            <Button 
              size="small" 
              onClick={loadAccessDetails}
              disabled={loadingDetails}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </Tooltip>
        </Box>

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Project: {project.name}
        </Typography>

        {loading && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading permissions: {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* Current Role */}
            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom>
                Your Role
              </Typography>
              <Chip
                label={permissions.userProjectRole || 'No Access'}
                color={getRoleColor(permissions.userProjectRole)}
                variant="outlined"
              />
            </Box>

            {/* Permission Breakdown */}
            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom>
                Permission Breakdown
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    {getPermissionIcon(permissions.canViewProject)}
                  </ListItemIcon>
                  <ListItemText 
                    primary="View Project" 
                    secondary={permissions.canViewProject ? "You can view this project" : "You cannot view this project"}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    {getPermissionIcon(permissions.canEditProject)}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Edit Project" 
                    secondary={permissions.canEditProject ? "You can edit project details" : "You cannot edit this project"}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    {getPermissionIcon(permissions.canManageProject)}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Manage Project" 
                    secondary={permissions.canManageProject ? "You can manage project settings and members" : "You cannot manage this project"}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    {getPermissionIcon(permissions.canManageMembers)}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Manage Members" 
                    secondary={permissions.canManageMembers ? "You can add/remove project members" : "You cannot manage project members"}
                  />
                </ListItem>
              </List>
            </Box>

            {/* Access Sources */}
            {accessDetails && (
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Access Sources
                </Typography>
                <List dense>
                  {accessDetails.access_sources.map((source, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <InfoIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={source.type}
                        secondary={source.source}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Organization Context */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Organization Context
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Organization Role" 
                    secondary={permissions.userOrgRole || 'No role'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Organization Admin" 
                    secondary={permissions.isOrgAdmin ? "Yes - You have admin privileges" : "No"}
                  />
                </ListItem>
              </List>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ProjectPermissionsPanel;
